/**
 * Reusable image uploader for V2 admin.
 *
 * - Drag-drop zone + click-to-browse for new uploads
 * - Multi-file selection
 * - Per-file validation (MIME + size)
 * - Parallel uploads with partial-success handling
 * - Thumbnail strip with remove + make-primary buttons per image
 * - Drag thumbnails to reorder (HTML5 native; uses a custom dataTransfer
 *   type so internal reorder doesn't trigger the file-upload dropzone)
 * - Best-effort delete from Storage when an admin-uploaded image is removed
 *
 * State is fully controlled: parent passes `value: string[]` (URLs) and
 * receives changes via `onChange`. The first URL is conventionally treated
 * as the primary image by callers (e.g. property cover).
 */
import { useRef, useState } from "react";
import {
  GripVertical,
  ImagePlus,
  Loader2,
  Star,
  Upload,
  X,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import {
  deletePropertyImageBestEffort,
  uploadPropertyImagesParallel,
  validateImageFile,
} from "@/lib/v2-upload";
import { useLanguage } from "@/contexts/LanguageContext";

type Props = {
  value: string[];
  onChange: (urls: string[]) => void;
  /** Soft cap. New uploads beyond this are rejected with a toast. */
  maxImages?: number;
  /** Compact mode trims paddings + uses smaller thumbnails. */
  compact?: boolean;
  /** Hide the "Primary" indicator on the first image. */
  hidePrimaryBadge?: boolean;
  /** Label rendered above the dropzone. */
  label?: string;
  /** Helper text rendered next to the label. */
  hint?: string;
  className?: string;
};

const ACCEPT = "image/jpeg,image/png,image/webp,image/avif";

// Custom dataTransfer mime so internal thumbnail reorder doesn't get
// confused with files dragged in from the OS. The dropzone's onDrop
// only acts on `dataTransfer.files`; the thumbnail reorder only acts
// when this exact type is present.
const REORDER_MIME = "application/x-coliviq-image-index";

export function ImageUploader({
  value,
  onChange,
  maxImages = 20,
  compact,
  hidePrimaryBadge,
  label,
  hint,
  className,
}: Props) {
  const { t } = useLanguage();
  const resolvedLabel = label ?? t("admin.rooms.photosLabel", "Photos");
  const [isUploading, setIsUploading] = useState(false);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  // Reorder state: source index of the thumbnail being dragged, and the
  // current drop-target index for visual feedback. Both null when no
  // internal drag is in progress.
  const [draggingIdx, setDraggingIdx] = useState<number | null>(null);
  const [dropTargetIdx, setDropTargetIdx] = useState<number | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const { toast } = useToast();

  function pickFiles() {
    inputRef.current?.click();
  }

  async function handleFiles(files: File[]) {
    if (files.length === 0) return;

    // Pre-filter against capacity
    const remaining = Math.max(0, maxImages - value.length);
    if (remaining === 0) {
      toast({
        title: t(
          "admin.rooms.maxImagesReached",
          "Maximum {{max}} images reached"
        ).replace("{{max}}", String(maxImages)),
        description: t(
          "admin.rooms.removeSomeBeforeAdding",
          "Remove some before adding more."
        ),
        variant: "destructive",
      });
      return;
    }
    let toUpload = files;
    if (files.length > remaining) {
      toast({
        title: t(
          "admin.rooms.onlyMoreAllowed",
          "Only {{remaining}} more allowed"
        ).replace("{{remaining}}", String(remaining)),
        description: (files.length - remaining === 1
          ? t("admin.rooms.fileSkippedCountOne", "{{count}} file was skipped.")
          : t(
              "admin.rooms.fileSkippedCountMany",
              "{{count}} files were skipped."
            )
        ).replace("{{count}}", String(files.length - remaining)),
      });
      toUpload = files.slice(0, remaining);
    }

    // Pre-validate (size / MIME) — surface invalid ones up front so the
    // user gets a single batched error rather than per-file toasts mid-upload.
    const valid: File[] = [];
    const invalid: { file: File; reason: string }[] = [];
    for (const f of toUpload) {
      const v = validateImageFile(f);
      if (v.ok) valid.push(f);
      else invalid.push({ file: f, reason: v.reason });
    }
    if (invalid.length > 0) {
      toast({
        title: (invalid.length === 1
          ? t("admin.rooms.fileSkippedTitleOne", "{{count}} file skipped")
          : t("admin.rooms.fileSkippedTitleMany", "{{count}} files skipped")
        ).replace("{{count}}", String(invalid.length)),
        description: invalid
          .slice(0, 3)
          .map((x) => `${x.file.name}: ${x.reason}`)
          .join("\n"),
        variant: "destructive",
      });
    }
    if (valid.length === 0) return;

    setIsUploading(true);
    try {
      const results = await uploadPropertyImagesParallel(valid);
      const successUrls = results
        .filter((r) => r.ok && r.url)
        .map((r) => r.url as string);
      const failed = results.filter((r) => !r.ok);

      if (successUrls.length > 0) {
        onChange([...value, ...successUrls]);
      }
      if (failed.length > 0) {
        toast({
          title: t(
            "admin.rooms.uploadsFailedTitle",
            "{{count}} upload{{plural}} failed"
          )
            .replace("{{count}}", String(failed.length))
            .replace("{{plural}}", failed.length === 1 ? "" : "s"),
          description: failed
            .slice(0, 3)
            .map(
              (f) =>
                `${f.file.name}: ${f.reason ?? t("common.unknownError", "Unknown error")}`
            )
            .join("\n"),
          variant: "destructive",
        });
      }
    } finally {
      setIsUploading(false);
    }
  }

  function onInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    void handleFiles(files);
    // Reset input so the same file can be re-selected after removal
    e.target.value = "";
  }

  function onDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setIsDraggingOver(false);
    const files = Array.from(e.dataTransfer.files ?? []).filter((f) =>
      f.type.startsWith("image/")
    );
    void handleFiles(files);
  }

  function onDragOver(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    if (!isDraggingOver) setIsDraggingOver(true);
  }

  function onDragLeave(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setIsDraggingOver(false);
  }

  function removeAt(index: number) {
    const removed = value[index];
    onChange(value.filter((_, i) => i !== index));
    void deletePropertyImageBestEffort(removed);
  }

  function makePrimary(index: number) {
    if (index === 0) return;
    const next = [...value];
    const [item] = next.splice(index, 1);
    next.unshift(item);
    onChange(next);
  }

  // ─── Reorder via native HTML5 drag-and-drop ────────────────────────────────
  // We use a custom dataTransfer mime (REORDER_MIME) so the dropzone above
  // — which listens for files from the OS — doesn't pick up these events.

  function onThumbDragStart(index: number) {
    return (e: React.DragEvent<HTMLLIElement>) => {
      setDraggingIdx(index);
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData(REORDER_MIME, String(index));
    };
  }

  function onThumbDragOver(index: number) {
    return (e: React.DragEvent<HTMLLIElement>) => {
      // Only accept our own drag, not file drops from the OS.
      if (!e.dataTransfer.types.includes(REORDER_MIME)) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
      if (dropTargetIdx !== index) setDropTargetIdx(index);
    };
  }

  function onThumbDrop(targetIndex: number) {
    return (e: React.DragEvent<HTMLLIElement>) => {
      const raw = e.dataTransfer.getData(REORDER_MIME);
      if (raw === "") return;
      e.preventDefault();
      e.stopPropagation();
      const sourceIndex = Number(raw);
      setDraggingIdx(null);
      setDropTargetIdx(null);
      if (
        Number.isNaN(sourceIndex) ||
        sourceIndex === targetIndex ||
        sourceIndex < 0 ||
        sourceIndex >= value.length
      ) {
        return;
      }
      const next = [...value];
      const [item] = next.splice(sourceIndex, 1);
      // Insert at targetIndex. When dragging right (sourceIndex < targetIndex),
      // the splice above shifted everything left by one, so targetIndex now
      // points at the item that was originally one to the right of the drop
      // target — which is the desired insert position. When dragging left,
      // targetIndex is unaffected. So no off-by-one fixup needed.
      next.splice(targetIndex, 0, item);
      onChange(next);
    };
  }

  function onThumbDragEnd() {
    setDraggingIdx(null);
    setDropTargetIdx(null);
  }

  const thumbSize = compact ? "h-20 w-20" : "h-24 w-24";

  return (
    <div className={cn("space-y-3", className)}>
      <header className="flex items-baseline justify-between gap-3">
        <div>
          {(resolvedLabel || value.length > 0) && (
            <p className="text-xs font-medium">
              {resolvedLabel}
              {value.length > 0 && (
                <span className="text-muted-foreground font-normal ml-1.5 tabular-nums">
                  ({value.length}/{maxImages})
                </span>
              )}
            </p>
          )}
          {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
        </div>
      </header>

      {/* Dropzone */}
      <Card
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onClick={pickFiles}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            pickFiles();
          }
        }}
        className={cn(
          "cursor-pointer border-2 border-dashed flex flex-col items-center justify-center text-center gap-2 transition-colors",
          compact ? "p-4" : "p-6",
          isDraggingOver
            ? "border-primary bg-primary/5"
            : "border-border hover:border-primary/50 hover:bg-muted/30",
          isUploading && "opacity-60 cursor-wait pointer-events-none"
        )}
      >
        {isUploading ? (
          <>
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              {t("common.uploading", "Uploading…")}
            </p>
          </>
        ) : (
          <>
            <div className="rounded-full bg-muted p-2 text-muted-foreground">
              <ImagePlus className={compact ? "h-4 w-4" : "h-5 w-5"} />
            </div>
            <p className="text-sm font-medium">
              {isDraggingOver
                ? t("admin.rooms.dropToUpload", "Drop to upload")
                : t(
                    "admin.rooms.dragImagesHere",
                    "Drag images here, or click to browse"
                  )}
            </p>
            <p className="text-xs text-muted-foreground">
              {t(
                "admin.rooms.imageFormatsHint",
                "JPG · PNG · WebP · AVIF — up to 10 MB each"
              )}
            </p>
          </>
        )}
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT}
          multiple
          onChange={onInputChange}
          className="hidden"
        />
      </Card>

      {/* Thumbnails */}
      {value.length > 0 && (
        <ul className="flex flex-wrap gap-2 list-none p-0 m-0">
          {value.map((url, i) => (
            <li
              key={`${url}-${i}`}
              draggable={value.length > 1}
              onDragStart={onThumbDragStart(i)}
              onDragOver={onThumbDragOver(i)}
              onDrop={onThumbDrop(i)}
              onDragEnd={onThumbDragEnd}
              onDragLeave={() => {
                if (dropTargetIdx === i) setDropTargetIdx(null);
              }}
              className={cn(
                "relative group rounded-md overflow-hidden border bg-muted transition-all",
                thumbSize,
                value.length > 1 && "cursor-grab active:cursor-grabbing",
                draggingIdx === i && "opacity-40",
                dropTargetIdx === i &&
                  draggingIdx !== null &&
                  draggingIdx !== i &&
                  "ring-2 ring-primary ring-offset-2"
              )}
              title={
                value.length > 1
                  ? t("admin.aria.dragToReorder", "Drag to reorder")
                  : undefined
              }
            >
              <img
                src={url}
                alt={`${t("admin.rooms.photoAlt", "Photo")} ${i + 1}`}
                loading="lazy"
                draggable={false}
                className="h-full w-full object-cover pointer-events-none"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
              {!hidePrimaryBadge && i === 0 && (
                <span className="absolute top-1 left-1 bg-background/90 text-[10px] font-medium px-1.5 py-0.5 rounded flex items-center gap-1">
                  <Star className="h-2.5 w-2.5 fill-current" />{" "}
                  {t("admin.rooms.primary", "Primary")}
                </span>
              )}
              {value.length > 1 && (
                <span
                  className="absolute top-1 right-1 bg-background/80 text-muted-foreground rounded p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                  aria-hidden
                >
                  <GripVertical className="h-3 w-3" />
                </span>
              )}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100">
                {i !== 0 && !hidePrimaryBadge && (
                  <Button
                    type="button"
                    size="icon"
                    variant="secondary"
                    className="h-7 w-7"
                    onClick={(e) => {
                      e.stopPropagation();
                      makePrimary(i);
                    }}
                    title={t("admin.rooms.makePrimary", "Make primary")}
                    aria-label={`${t("admin.rooms.makeImagePrimaryAria", "Make image {{n}} primary").replace("{{n}}", String(i + 1))}`}
                  >
                    <Star className="h-3.5 w-3.5" />
                  </Button>
                )}
                <Button
                  type="button"
                  size="icon"
                  variant="destructive"
                  className="h-7 w-7"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeAt(i);
                  }}
                  title={t("admin.rooms.removeImage", "Remove image")}
                  aria-label={`${t("admin.rooms.removeImageAria", "Remove image {{n}}").replace("{{n}}", String(i + 1))}`}
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              </div>
            </li>
          ))}
          {value.length < maxImages && !isUploading && (
            <li>
              <button
                type="button"
                onClick={pickFiles}
                className={cn(
                  "rounded-md border-2 border-dashed flex flex-col items-center justify-center text-muted-foreground hover:border-primary/50 hover:text-foreground transition-colors",
                  thumbSize
                )}
                aria-label={t(
                  "admin.rooms.addMoreImagesAria",
                  "Add more images"
                )}
              >
                <Upload className="h-4 w-4" />
                <span className="text-[10px] mt-1">
                  {t("admin.rooms.addMore", "Add more")}
                </span>
              </button>
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
